import { useState, useRef, useEffect } from "react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { CalendarIcon, Clock, ChevronUp, ChevronDown } from "lucide-react";
import { da } from "date-fns/locale";

const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const minutes = ["00", "15", "30", "45"];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("da-DK", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

const formatTime = (dateStr) => {
  if (!dateStr) return "12:00";
  const d = new Date(dateStr);
  if (isNaN(d)) return "12:00";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const TimeScroller = ({ value, onChange }) => {
  const [h, m] = (value || "12:00").split(":").map(Number);
  const hourRef = useRef(null);
  const minRef = useRef(null);

  useEffect(() => {
    if (hourRef.current) {
      const el = hourRef.current.children[h];
      if (el) el.scrollIntoView({ block: "center", behavior: "auto" });
    }
  }, [h]);

  const setHour = (newH) => {
    const hh = ((newH % 24) + 24) % 24;
    onChange(`${String(hh).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  };

  const setMin = (newM) => {
    onChange(`${String(h).padStart(2, "0")}:${String(newM).padStart(2, "0")}`);
  };

  return (
    <div className="flex items-center gap-1 justify-center">
      {/* Hours */}
      <div className="flex flex-col items-center">
        <button type="button" onClick={() => setHour(h + 1)} className="p-1 text-gray-400 hover:text-white"><ChevronUp size={16} /></button>
        <div ref={hourRef} className="h-[120px] w-12 overflow-y-auto scrollbar-thin flex flex-col items-center snap-y snap-mandatory" style={{ scrollbarWidth: "none" }}>
          {hours.map((hr) => (
            <button key={hr} type="button" onClick={() => setHour(Number(hr))}
              className={`w-full py-1.5 text-center text-sm snap-center rounded transition-all ${Number(hr) === h ? "bg-[#00d4ff]/20 text-[#00d4ff] font-bold" : "text-gray-500 hover:text-white"}`}>
              {hr}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setHour(h - 1)} className="p-1 text-gray-400 hover:text-white"><ChevronDown size={16} /></button>
      </div>

      <span className="text-2xl font-bold text-white mt-[-4px]">:</span>

      {/* Minutes */}
      <div className="flex flex-col items-center">
        <button type="button" onClick={() => setMin((m + 15) % 60)} className="p-1 text-gray-400 hover:text-white"><ChevronUp size={16} /></button>
        <div ref={minRef} className="flex flex-col items-center gap-0.5">
          {minutes.map((mn) => (
            <button key={mn} type="button" onClick={() => setMin(Number(mn))}
              className={`w-12 py-1.5 text-center text-sm rounded transition-all ${Number(mn) === m ? "bg-[#00d4ff]/20 text-[#00d4ff] font-bold" : "text-gray-500 hover:text-white"}`}>
              {mn}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setMin(((m - 15) % 60 + 60) % 60)} className="p-1 text-gray-400 hover:text-white"><ChevronDown size={16} /></button>
      </div>
    </div>
  );
};

const DateTimePicker = ({ value, onChange, label, required, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  const currentDate = value ? new Date(value) : null;
  const currentTime = formatTime(value);

  const handleDateSelect = (day) => {
    if (!day) return;
    const [hh, mm] = currentTime.split(":").map(Number);
    const d = new Date(day);
    d.setHours(hh, mm, 0, 0);
    // Format as datetime-local string
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    const hr = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    onChange(`${yr}-${mo}-${dy}T${hr}:${mi}`);
  };

  const handleTimeChange = (timeStr) => {
    const [hh, mm] = timeStr.split(":").map(Number);
    const base = currentDate ? new Date(currentDate) : new Date();
    base.setHours(hh, mm, 0, 0);
    const yr = base.getFullYear();
    const mo = String(base.getMonth() + 1).padStart(2, "0");
    const dy = String(base.getDate()).padStart(2, "0");
    const hr = String(base.getHours()).padStart(2, "0");
    const mi = String(base.getMinutes()).padStart(2, "0");
    onChange(`${yr}-${mo}-${dy}T${hr}:${mi}`);
  };

  return (
    <div>
      {label && <label className="block text-gray-300 mb-2">{label} {required && "*"}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="mc-input w-full text-left flex items-center gap-3 px-4 py-3" data-testid={testId}>
            <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            {currentDate ? (
              <span className="text-white">{formatDate(value)} <span className="text-[#00d4ff] font-mono ml-2">{currentTime}</span></span>
            ) : (
              <span className="text-gray-500">{placeholder || "Vælg dato og tid"}</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#0f0f12] border border-gray-700" align="start" sideOffset={8}>
          <div className="flex flex-col sm:flex-row">
            <Calendar
              mode="single"
              selected={currentDate}
              onSelect={handleDateSelect}
              locale={da}
              className="border-b sm:border-b-0 sm:border-r border-gray-700/50"
              classNames={{
                months: "flex flex-col",
                month: "space-y-3",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-sm font-medium text-white",
                nav: "space-x-1 flex items-center",
                nav_button: "h-7 w-7 bg-transparent border border-gray-700 rounded p-0 text-gray-400 hover:text-white hover:border-gray-500 inline-flex items-center justify-center",
                nav_button_previous: "absolute left-1",
                nav_button_next: "absolute right-1",
                table: "w-full border-collapse",
                head_row: "flex",
                head_cell: "text-gray-500 rounded-md w-9 font-normal text-[0.8rem]",
                row: "flex w-full mt-1",
                cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#00d4ff]/20 [&:has([aria-selected])]:rounded-md",
                day: "h-9 w-9 p-0 font-normal text-gray-300 hover:bg-gray-700 rounded-md inline-flex items-center justify-center cursor-pointer",
                day_selected: "bg-[#00d4ff] text-black hover:bg-[#00d4ff] hover:text-black font-bold",
                day_today: "bg-gray-800 text-white font-bold",
                day_outside: "text-gray-600",
                day_disabled: "text-gray-700",
              }}
            />
            <div className="p-4 flex flex-col items-center justify-center min-w-[100px]">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-400 uppercase tracking-wider font-['Oswald']">Tid</span>
              </div>
              <TimeScroller value={currentTime} onChange={handleTimeChange} />
            </div>
          </div>
          <div className="p-2 border-t border-gray-700/50 flex justify-end">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-1.5 text-sm bg-[#00d4ff]/20 text-[#00d4ff] hover:bg-[#00d4ff]/30 rounded transition-colors">
              Færdig
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateTimePicker;
